/**
 * Commercial Invoice Intake — import an invoice that has ALREADY been sent.
 *
 * WHAT THIS SCREEN IS FOR
 * -----------------------
 * An operator has a PDF of an invoice the customer has already received.
 * Pulse's commercial position needs to reflect it. Before this screen,
 * that meant a developer and a SQL session.
 *
 * THE THING THE DESIGN IS BUILT AROUND
 * ------------------------------------
 * Two numbers that look alike and are not:
 *
 *     AMOUNT INVOICED NOW      what the document charged
 *     ANNUAL RECURRING VALUE   what changes at renewal
 *
 * Ten extra users added six months into a term are billed GBP 3,000 and
 * are worth GBP 6,000 a year. They appear side by side, labelled, at
 * every step. Nowhere does one stand in for the other.
 *
 * THE PROMISE THE SCREEN REPEATS
 * ------------------------------
 * The invoice has already been issued and already been sent, so Pulse
 * issues nothing and sends nothing. That is said in plain words on the
 * confirmation step rather than left as something the operator has to
 * take on trust — and the server guarantees it structurally
 * (`routes/billing_import.py`), so the words are true rather than
 * reassuring.
 *
 * Accessibility: every state is conveyed by words and an icon, never by
 * colour alone; the impact figures are a description list, not a
 * decorative layout; the stepper is a real tablist.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  ArrowRight, ArrowsClockwise, CheckCircle, CloudArrowUp, Eye, FileText,
  Info, Prohibit, Trash, Warning,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { money } from "./BillingSpecificationPanels";
import {
  WIZARD_STEPS, describeLine, formatDelta, furthestStep, hasBlockingDuplicate,
  impactSummary, outstandingLines,
} from "@/lib/commercialImpact";

const BLUE = "#0055FF";

/* ── Small shared pieces ───────────────────────────────────────────── */

function EmptyState({ title, body, action }) {
  return (
    <div className="border border-dashed border-[#E5E5E5] bg-[#FAFAFA] p-8 text-center">
      <p className="text-sm font-bold text-[#0A0A0A]">{title}</p>
      <p className="text-xs text-[#525252] mt-1.5 max-w-xl mx-auto leading-relaxed">{body}</p>
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

/**
 * Instructions, stated as what to do and what will follow from it.
 * Michael asked for this explicitly and it is not decoration: an
 * operator who does not know the consequence cannot give informed
 * approval, and informed approval is the only control standing between
 * an AI reading and the renewal basis.
 */
function Guidance({ title, children, tone = "info" }) {
  const meta = {
    info: { Icon: Info, cls: "border-[#BFDBFE] bg-[#EFF6FF] text-[#1E3A8A]" },
    warn: { Icon: Warning, cls: "border-[#FDE68A] bg-[#FFFBEB] text-[#92400E]" },
    stop: { Icon: Prohibit, cls: "border-[#FECACA] bg-[#FEF2F2] text-[#991B1B]" },
  }[tone];
  const { Icon } = meta;
  return (
    <div className={`border p-3 flex gap-2.5 ${meta.cls}`} role="note">
      <Icon size={15} weight="bold" className="mt-0.5 shrink-0" aria-hidden="true" />
      <div className="text-[11px] leading-relaxed">
        {title && <div className="font-bold mb-0.5">{title}</div>}
        {children}
      </div>
    </div>
  );
}

function Tile({ label, value, hint, emphasis }) {
  return (
    <div className={`border p-4 ${emphasis ? `border-2 border-[${BLUE}] bg-[#EFF6FF]` : "border-[#E5E5E5] bg-white"}`}>
      <div className={`text-[10px] font-bold uppercase tracking-wider ${emphasis ? `text-[${BLUE}]` : "text-[#A3A3A3]"}`}>
        {label}
      </div>
      <div className="text-xl font-black mt-1 tracking-tight text-[#0A0A0A]">{value}</div>
      {hint && <div className="text-[11px] text-[#A3A3A3] mt-0.5">{hint}</div>}
    </div>
  );
}

/**
 * The primary visual: what it is now, what it becomes.
 * Rendered as a description list so a screen reader announces the pair
 * rather than three unrelated numbers.
 */
function BeforeAfter({ label, before, after, movement, testId }) {
  return (
    <div className="border-2 border-[#E5E5E5] bg-white p-5" data-testid={testId}>
      <dt className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">
        {label}
      </dt>
      <dd className="mt-2 flex flex-wrap items-center gap-3">
        <span className="text-lg font-bold text-[#737373] line-through decoration-1">
          {before}
        </span>
        <ArrowRight size={18} weight="bold" className="text-[#A3A3A3]" aria-hidden="true" />
        <span className={`text-2xl font-black tracking-tight ${movement?.changed ? `text-[${BLUE}]` : "text-[#0A0A0A]"}`}>
          {after}
        </span>
      </dd>
      {movement && (
        <div className="text-[11px] font-bold mt-1.5 uppercase tracking-wider text-[#525252]">
          {movement.direction === "unchanged" ? "Unchanged" : `${movement.direction}`}
        </div>
      )}
    </div>
  );
}

const KB = 1024;
const fileSize = (n) => {
  const b = Number(n) || 0;
  if (b < KB) return `${b} B`;
  if (b < KB * KB) return `${(b / KB).toFixed(0)} KB`;
  return `${(b / KB / KB).toFixed(1)} MB`;
};

const DUPLICATE_META = {
  EXACT_DUPLICATE: {
    label: "Already imported",
    tone: "stop",
    cls: "bg-[#FEE2E2] text-[#991B1B]",
  },
  POSSIBLE_REVISION: {
    label: "Possible revision",
    tone: "warn",
    cls: "bg-[#FEF3C7] text-[#92400E]",
  },
  POSSIBLE_DUPLICATE: {
    label: "Possible duplicate",
    tone: "warn",
    cls: "bg-[#FEF3C7] text-[#92400E]",
  },
};

const EXTRACTION_META = {
  pending: { label: "Not yet read", Icon: FileText, cls: "bg-[#F5F5F5] text-[#525252]" },
  extracted: { label: "Read", Icon: CheckCircle, cls: "bg-[#DCFCE7] text-[#166534]" },
  needs_review: { label: "Read — needs review", Icon: Warning, cls: "bg-[#FEF3C7] text-[#92400E]" },
  failed: { label: "Could not be read", Icon: Prohibit, cls: "bg-[#FEE2E2] text-[#991B1B]" },
};

function Chip({ meta, testId }) {
  const Icon = meta.Icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${meta.cls}`}
      data-testid={testId}
    >
      {Icon && <Icon size={11} weight="bold" aria-hidden="true" />}
      {meta.label}
    </span>
  );
}

/**
 * A duplicate warning, with the ruling the operator has to make on it.
 *
 * Soft warnings (a possible revision, a possible duplicate) are raised
 * AFTER the document is read, because until then Pulse only knows the
 * file's content hash — not its invoice number or total. So this appears
 * on the interpretation step as well as the upload list, and it must
 * carry a way to resolve it: a warning with no answer available is a
 * dead end that stops the import for good.
 *
 * An exact duplicate is deliberately NOT resolvable here. Byte-identical
 * to a document already imported is the strongest signal there is, and
 * the only correct answer is to remove the file.
 */
function DuplicateNotice({ file, onAcknowledge, busy }) {
  const meta = DUPLICATE_META[file.duplicate_verdict];
  if (!meta) return null;
  const blocking = file.duplicate_verdict === "EXACT_DUPLICATE";
  const settled = file.duplicate_acknowledged && !blocking;
  return (
    <div className="mt-1 space-y-1.5" data-testid={`dup-notice-${file.id}`}>
      <Chip meta={meta} testId={`dup-${file.id}`} />
      <div className="text-[11px] text-[#92400E] leading-relaxed">
        {file.duplicate_detail}
      </div>
      {blocking ? (
        <div className="text-[11px] font-bold text-[#991B1B]">
          Remove this document to continue — this cannot be overridden.
        </div>
      ) : settled ? (
        <div className="text-[11px] font-bold text-[#166534] inline-flex items-center gap-1">
          <CheckCircle size={12} weight="bold" aria-hidden="true" />
          Confirmed as a distinct document
        </div>
      ) : (
        <Button
          type="button"
          variant="outline"
          className="h-7 rounded-none text-[10px] uppercase font-bold tracking-wider"
          onClick={() => onAcknowledge(file)}
          disabled={busy}
          data-testid={`import-ack-dup-${file.id}`}
        >
          This is a different document — continue
        </Button>
      )}
    </div>
  );
}

/* ── Step 1 — Upload ───────────────────────────────────────────────── */

function UploadStep({ session, vocabulary, onUpload, onRemove, onAcknowledge,
                     onContinue, busy }) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);
  const files = session?.files || [];
  const accepted = (vocabulary?.accepted_extensions || []).join(", ");
  const maxMb = Math.round((vocabulary?.max_upload_bytes || 0) / 1024 / 1024);

  const take = (list) => {
    const picked = Array.from(list || []);
    if (picked.length) onUpload(picked);
  };

  return (
    <div className="space-y-4">
      <Guidance title="What to do">
        Drag the invoices you want to record onto the area below, or choose them
        from your computer. Pulse stores each document, reads it, and proposes
        what it means commercially. <strong>Nothing changes yet</strong> — you
        review and approve every proposal before anything is applied.
      </Guidance>

      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); take(e.dataTransfer.files); }}
        className={`border-2 border-dashed p-10 text-center transition-colors ${
          dragging ? `border-[${BLUE}] bg-[#EFF6FF]` : "border-[#D4D4D4] bg-[#FAFAFA]"
        }`}
        data-testid="import-dropzone"
      >
        <CloudArrowUp size={32} weight="light" className="mx-auto text-[#A3A3A3]" aria-hidden="true" />
        <p className="text-sm font-bold text-[#0A0A0A] mt-2">
          Drop invoices here
        </p>
        <p className="text-[11px] text-[#525252] mt-1">
          {accepted || "PDF or image"} · up to {maxMb || 20} MB each
          {vocabulary?.max_files_per_import
            ? ` · up to ${vocabulary.max_files_per_import} documents per import`
            : ""}
        </p>
        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          accept={accepted}
          onChange={(e) => { take(e.target.files); e.target.value = ""; }}
          data-testid="import-file-input"
        />
        <Button
          type="button"
          variant="outline"
          className="mt-4 rounded-none h-8 text-[11px] uppercase font-bold tracking-wider"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          data-testid="import-choose-files"
        >
          {busy ? "Uploading…" : "Choose files"}
        </Button>
      </div>

      {files.length === 0 ? (
        <EmptyState
          title="No documents added yet"
          body={
            "Add the invoice or invoices you want Pulse to record. Several "
            + "documents can be imported together — Pulse reads them as one "
            + "commercial history rather than as unrelated charges."
          }
        />
      ) : (
        <table className="w-full text-xs border border-[#E5E5E5]" data-testid="import-file-table">
          <caption className="sr-only">Documents in this import</caption>
          <thead className="bg-[#FAFAFA]">
            <tr className="text-left text-[10px] uppercase tracking-wider text-[#737373]">
              <th scope="col" className="px-3 py-2 font-bold">Document</th>
              <th scope="col" className="px-3 py-2 font-bold">Size</th>
              <th scope="col" className="px-3 py-2 font-bold">Fingerprint</th>
              <th scope="col" className="px-3 py-2 font-bold">Status</th>
              <th scope="col" className="px-3 py-2 font-bold text-right">Remove</th>
            </tr>
          </thead>
          <tbody>
            {files.map((f) => {
              return (
                <tr key={f.id} className="border-t border-[#F0F0F0] align-top">
                  <td className="px-3 py-2">
                    <div className="font-bold text-[#0A0A0A]">{f.file_name}</div>
                    <DuplicateNotice
                      file={f} onAcknowledge={onAcknowledge} busy={busy} />
                  </td>
                  <td className="px-3 py-2 text-[#525252]">{fileSize(f.file_size)}</td>
                  <td className="px-3 py-2 font-mono text-[10px] text-[#A3A3A3]"
                      title={f.sha256}>
                    {(f.sha256 || "").slice(0, 12)}…
                  </td>
                  <td className="px-3 py-2">
                    <Chip meta={EXTRACTION_META[f.extraction_status] || EXTRACTION_META.pending} />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button
                      type="button"
                      variant="ghost"
                      className="h-7 px-2 rounded-none text-[#991B1B] hover:bg-[#FEE2E2]"
                      onClick={() => onRemove(f)}
                      disabled={busy}
                      aria-label={`Remove ${f.file_name}`}
                      data-testid={`import-remove-${f.id}`}
                    >
                      <Trash size={14} weight="bold" aria-hidden="true" />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {files.length > 0 && !hasBlockingDuplicate(session) && (
        <div className="flex justify-end">
          <Button
            type="button"
            onClick={onContinue}
            disabled={busy}
            className={`bg-[${BLUE}] hover:bg-[#003ECC] text-white rounded-none h-9 text-[11px] uppercase font-bold tracking-wider`}
            data-testid="import-upload-continue"
          >
            Continue to interpretation
            <ArrowRight size={14} weight="bold" className="ml-1.5" aria-hidden="true" />
          </Button>
        </div>
      )}

      {hasBlockingDuplicate(session) && (
        <Guidance title="One of these documents has already been imported" tone="stop">
          Pulse recognises it byte for byte from an earlier import. Importing it
          again would record the same commercial change twice. Remove it to
          continue — this cannot be overridden.
        </Guidance>
      )}
    </div>
  );
}

/**
 * "Is this the document that started the relationship?"
 *
 * Pulse PROPOSES the earliest document on file, because document date is
 * how commercial history is ordered — upload order means nothing. But a
 * proposal is all it is. The earliest invoice an operator HOLDS is not
 * necessarily the earliest ever RAISED, so the origin is confirmed by a
 * person, and every reason to disagree is put in front of them first.
 */
function OriginNomination({ file, session, onNominate, busy }) {
  const proposal = session?.initial_proposal || {};
  const isCandidate = proposal.candidate_file_id === file.id;
  const role = session?.initial_subscription_role || "initial_subscription";
  const nominated = file.commercial_role === role;
  const someoneElse = session?.nominated_initial_file_id
    && session.nominated_initial_file_id !== file.id;
  const bootstrap = session?.mode === "bootstrap";
  const rebase = session?.rebase;
  const isEarlierThanOrigin = (rebase?.earlier_documents || [])
    .some((d) => d.file_id === file.id);

  // Only ask where the answer could matter: on a client with no origin
  // yet, or on a document that predates the recorded one.
  if (!bootstrap && !isEarlierThanOrigin) return null;
  if (bootstrap && !isCandidate && !nominated && someoneElse) return null;

  return (
    <div className={`border p-3 mt-3 ${nominated
      ? "border-[#166534] bg-[#F0FDF4]" : "border-[#E5E5E5] bg-[#FAFAFA]"}`}
      data-testid={`origin-nomination-${file.id}`}>
      <div className="text-[11px] font-bold text-[#0A0A0A]">
        {isEarlierThanOrigin
          ? "Is this the original invoice we sent this client?"
          : "Is this the invoice that started this client\u2019s relationship?"}
      </div>

      {isCandidate && proposal.reason && (
        <p className="text-[11px] text-[#525252] mt-1 leading-relaxed">
          {proposal.reason}{" "}
          <strong>Pulse proposes treating it as the original billing
          invoice \u2014 what we initially sold them.</strong> Is that correct?
        </p>
      )}
      {!isCandidate && !isEarlierThanOrigin && (
        <p className="text-[11px] text-[#525252] mt-1 leading-relaxed">
          This is not the earliest document on file, so Pulse does not
          propose it as the original sale \u2014 but you can mark it if you
          know better.
        </p>
      )}

      {isCandidate && (proposal.contradictions || []).map((c, i) => (
        <div key={i} className="mt-2">
          <Guidance tone="warn">{c}</Guidance>
        </div>
      ))}

      <div className="mt-2 flex flex-wrap gap-2 items-center">
        <Button
          type="button"
          className={`rounded-none h-8 text-[10px] uppercase font-bold tracking-wider ${
            nominated ? "bg-[#166534] hover:bg-[#14532D] text-white"
                      : `bg-[${BLUE}] hover:bg-[#003ECC] text-white`}`}
          onClick={() => onNominate(file, true)}
          disabled={busy || nominated || someoneElse}
          data-testid={`nominate-initial-${file.id}`}
        >
          {nominated
            ? "Yes \u2014 treated as the original invoice"
            : isEarlierThanOrigin
              ? "Yes \u2014 correct the billing history"
              : "Yes \u2014 treat as original invoice"}
        </Button>
        <Button
          type="button"
          variant="outline"
          className="rounded-none h-8 text-[10px] uppercase font-bold tracking-wider"
          onClick={() => onNominate(file, false)}
          disabled={busy || (!nominated && !isCandidate && !isEarlierThanOrigin)}
          data-testid={`reject-initial-${file.id}`}
        >
          {isEarlierThanOrigin
            ? "No \u2014 keep the existing original"
            : "No \u2014 this is a later invoice"}
        </Button>
        {someoneElse && (
          <span className="text-[11px] text-[#737373]">
            Another document is already marked as the original sale.
          </span>
        )}
      </div>

      <p className="text-[11px] mt-2 leading-relaxed text-[#525252]">
        {nominated
          ? "Pulse will record an Initial Billing Specification from this "
            + "document. That record is permanent once applied."
          : "Left unmarked, Pulse records this invoice in its correct "
            + "historical role and makes no claim about what was originally "
            + "sold."}
      </p>
      {/* Answering this question never changes WHAT the document is —
          only whether it is the origin. Showing the classification here
          makes that separation visible. */}
      {file.commercial_role && file.commercial_role !== role && (
        <p className="text-[10px] mt-1 text-[#737373]">
          Classified as <strong>{file.commercial_role.replace(/_/g, " ")}</strong>
          {" "}— answering this question does not change that.
        </p>
      )}
    </div>
  );
}

/**
 * The chronology Pulse is reading, oldest first.
 *
 * Shown before Apply so the operator can see the order commercial
 * meaning is being derived from — which is document date, never the
 * order the files happened to be dragged in.
 */
function Chronology({ session }) {
  const chron = session?.initial_proposal?.chronology || [];
  if (chron.length < 2) return null;
  return (
    <div className="border border-[#E5E5E5] bg-white" data-testid="import-chronology">
      <div className="px-4 py-2 border-b border-[#F0F0F0] bg-[#FAFAFA] text-[10px] font-bold uppercase tracking-wider text-[#737373]">
        Chronology — ordered by document date, not upload order
      </div>
      <ol className="divide-y divide-[#F0F0F0]">
        {chron.map((d) => (
          <li key={d.file_id} className="px-4 py-2 flex flex-wrap gap-2 items-baseline">
            <span className="text-[10px] font-bold text-[#A3A3A3] w-5">{d.position}</span>
            <span className="font-mono text-[11px] text-[#525252]">
              {d.document_date || "no date"}
            </span>
            <span className="text-xs font-bold text-[#0A0A0A]">
              {d.document_number || d.file_name}
            </span>
            {session.initial_proposal.candidate_file_id === d.file_id && (
              <span className="text-[10px] font-bold uppercase tracking-wider text-[#0055FF]">
                earliest on file
              </span>
            )}
            {session.nominated_initial_file_id === d.file_id && (
              <span className="text-[10px] font-bold uppercase tracking-wider text-[#166534]">
                marked as original sale
              </span>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}

/**
 * An earlier invoice has turned up than the one currently recorded as
 * the original sale. Correcting that is a commercial history REBASE, so
 * both records are put side by side before anything is decided.
 */
function RebaseNotice({ session }) {
  const rb = session?.rebase;
  if (!rb) return null;
  const cur = rb.current_origin || {};
  const earlier = rb.earlier_documents || [];
  return (
    <div className="border-2 border-[#F59E0B] bg-[#FFFBEB] p-4"
         data-testid="rebase-notice" role="note">
      <div className="text-xs font-bold text-[#92400E]">{rb.headline}</div>
      <dl className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
        <div className="border border-[#FDE68A] bg-white p-3">
          <dt className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">
            Current original
          </dt>
          <dd className="mt-1 text-xs text-[#0A0A0A]">
            <div className="font-bold">{cur.document_number || cur.file_name || "—"}</div>
            <div className="font-mono text-[11px] text-[#525252]">
              {cur.document_date || "no date"}
            </div>
            {cur.billed_amount != null && (
              <div className="text-[11px] text-[#525252]">
                {money(cur.billed_amount, cur.currency_code)}
              </div>
            )}
          </dd>
        </div>
        <div className="border border-[#FDE68A] bg-white p-3">
          <dt className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">
            New earlier document{earlier.length > 1 ? "s" : ""}
          </dt>
          <dd className="mt-1 text-xs text-[#0A0A0A] space-y-1">
            {earlier.map((d) => (
              <div key={d.file_id}>
                <div className="font-bold">{d.document_number || d.file_name}</div>
                <div className="font-mono text-[11px] text-[#525252]">
                  {d.document_date}
                </div>
                {d.document_total != null && (
                  <div className="text-[11px] text-[#525252]">
                    {money(d.document_total, d.currency_code)}
                  </div>
                )}
              </div>
            ))}
          </dd>
        </div>
      </dl>
      <p className="text-[11px] text-[#92400E] mt-3 leading-relaxed">
        {rb.requested
          ? "You have marked the earlier document as the original sale. "
            + "Applying will rebuild the billing history around it. The "
            + "existing record is kept as provenance, not deleted."
          : "Review the interpretation below, then decide. Doing nothing "
            + "keeps the existing original invoice."}
      </p>
    </div>
  );
}

/* ── Step 2 — Interpretation ───────────────────────────────────────── */

function InterpretStep({ session, onInterpret, onDownload, onAcknowledge,
                        onNominate, busy }) {
  const files = session?.files || [];
  const unread = files.filter((f) => f.extraction_status === "pending");
  const failed = files.filter((f) => f.extraction_status === "failed");

  return (
    <div className="space-y-4">
      <RebaseNotice session={session} />
      <Chronology session={session} />

      <Guidance title="What Pulse does here">
        Pulse reads each document and proposes what it says: the invoice number,
        the dates, and each charge on it. It <strong>proposes</strong> only —
        every figure that reaches your commercial records is calculated by Pulse
        from your confirmed classification on the next step, never taken from
        the reading.
      </Guidance>

      <div className="flex flex-wrap gap-2 items-center">
        <Button
          type="button"
          onClick={() => onInterpret(false)}
          disabled={busy || !unread.length}
          className={`bg-[${BLUE}] hover:bg-[#003ECC] text-white rounded-none h-8 text-[11px] uppercase font-bold tracking-wider`}
          data-testid="import-interpret"
        >
          {busy
            ? "Reading documents…"
            : unread.length
              ? `Read ${unread.length} document${unread.length === 1 ? "" : "s"}`
              : "All documents read"}
        </Button>
        {files.length > 0 && !unread.length && (
          <Button
            type="button"
            variant="outline"
            onClick={() => onInterpret(true)}
            disabled={busy}
            className="rounded-none h-8 text-[11px] uppercase font-bold tracking-wider"
            data-testid="import-reinterpret"
          >
            <ArrowsClockwise size={13} weight="bold" className="mr-1" aria-hidden="true" />
            Read again
          </Button>
        )}
      </div>

      {busy && (
        <div className="text-[11px] text-[#525252]" role="status">
          Reading each document. A scanned invoice can take up to a minute.
        </div>
      )}

      {failed.length > 0 && (
        <Guidance title="Some documents could not be read" tone="warn">
          {failed.map((f) => (
            <div key={f.id} className="mt-1">
              <strong>{f.file_name}</strong> — {f.extraction_error}
            </div>
          ))}
          <div className="mt-1.5">
            Remove them, or replace them with a clearer copy. An import cannot be
            applied while a document is unreadable.
          </div>
        </Guidance>
      )}

      {files.length === 0 ? (
        <EmptyState
          title="Nothing to read yet"
          body="Go back and add at least one invoice."
        />
      ) : (
        files.map((f) => (
          <div key={f.id} className="border border-[#E5E5E5] bg-white">
            <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-[#F0F0F0] bg-[#FAFAFA]">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-xs text-[#0A0A0A]">{f.file_name}</span>
                  <Chip meta={EXTRACTION_META[f.extraction_status] || EXTRACTION_META.pending} />
                </div>
                {/* Reading the document is what reveals its number and
                    total, so a possible revision can only surface here. */}
                <DuplicateNotice
                  file={f} onAcknowledge={onAcknowledge} busy={busy} />
              </div>
              <Button
                type="button"
                variant="outline"
                className="h-7 rounded-none text-[10px] uppercase font-bold tracking-wider"
                onClick={() => onDownload(f)}
                data-testid={`import-view-${f.id}`}
              >
                <Eye size={12} weight="bold" className="mr-1" aria-hidden="true" />
                View document
              </Button>
            </div>
            <dl className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-2 px-4 py-3 text-xs">
              {[
                ["Invoice number", f.document_number],
                ["Invoice date", f.document_date],
                ["Currency", f.document_currency],
                ["Invoice total", f.document_total != null
                  ? money(f.document_total, f.document_currency) : null],
                ["Service period", f.service_period_start
                  ? `${f.service_period_start} → ${f.service_period_end || "—"}` : null],
                ["PO / reference", f.purchase_order_ref],
              ].map(([label, value]) => (
                <div key={label}>
                  <dt className="text-[10px] uppercase tracking-wider text-[#A3A3A3] font-bold">
                    {label}
                  </dt>
                  <dd className="text-[#0A0A0A]">{value || <span className="text-[#D4D4D4]">Not stated</span>}</dd>
                </div>
              ))}
            </dl>
            <div className="px-4 pb-1">
              <OriginNomination file={f} session={session}
                                onNominate={onNominate} busy={busy} />
            </div>
            {f.interpretation_notes && (
              <div className="px-4 pb-3">
                <Guidance title="How Pulse read this document" tone={
                  f.extraction_status === "needs_review" ? "warn" : "info"
                }>
                  {f.interpretation_notes}
                </Guidance>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}

/* ── Step 3 — Classification ───────────────────────────────────────── */

function LineEditor({ line, vocabulary, recurringItems, currency, onSave, busy }) {
  const cats = vocabulary?.categories || [];
  const [form, setForm] = useState(() => ({
    commercial_category: line.commercial_category,
    annual_recurring_value: line.annual_recurring_value ?? 0,
    licensed_user_delta: line.licensed_user_delta ?? "",
    amount_invoiced: line.amount_invoiced ?? 0,
    target_recurring_item_id: line.target_recurring_item_id || "",
    operator_note: line.operator_note || "",
  }));
  useEffect(() => {
    setForm({
      commercial_category: line.commercial_category,
      annual_recurring_value: line.annual_recurring_value ?? 0,
      licensed_user_delta: line.licensed_user_delta ?? "",
      amount_invoiced: line.amount_invoiced ?? 0,
      target_recurring_item_id: line.target_recurring_item_id || "",
      operator_note: line.operator_note || "",
    });
  }, [line]);

  const chosen = cats.find((c) => c.value === form.commercial_category);
  const recurring = !!chosen?.recurring;

  const save = () => {
    const payload = {
      commercial_category: form.commercial_category,
      billing_period: recurring ? "year" : "one_time",
      annual_recurring_value: recurring ? Number(form.annual_recurring_value || 0) : 0,
      amount_invoiced: Number(form.amount_invoiced || 0),
      resolve_review: true,
    };
    if (recurring) {
      payload.licensed_user_delta = form.licensed_user_delta === ""
        ? null : Number(form.licensed_user_delta);
      if (form.target_recurring_item_id) {
        payload.target_recurring_item_id = form.target_recurring_item_id;
      } else {
        payload.clear_target = true;
      }
    } else {
      payload.clear_target = true;
    }
    if (form.operator_note) payload.operator_note = form.operator_note;
    onSave(line.id, payload);
  };

  return (
    <div className="border border-[#E5E5E5] bg-white" data-testid={`import-line-${line.id}`}>
      <div className="px-4 py-3 border-b border-[#F0F0F0] bg-[#FAFAFA] flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="font-bold text-xs text-[#0A0A0A]">{line.description}</div>
          <div className="text-[11px] text-[#737373] mt-0.5">
            As printed: {line.document_quantity ?? "—"} ×{" "}
            {line.document_unit_price != null
              ? money(line.document_unit_price, currency) : "—"}
            {" · charged "}
            {money(line.amount_invoiced, currency)}
          </div>
        </div>
        {line.needs_review && !line.operator_corrected ? (
          <Chip meta={{ label: "Needs review", Icon: Warning,
                        cls: "bg-[#FEF3C7] text-[#92400E]" }} />
        ) : line.operator_corrected ? (
          /* The server has tracked this all along; it was simply never
             rendered, so saving a line produced no visible change and
             the operator could not tell whether it had worked. */
          <Chip meta={{
            label: line.differs_from_ai ? "Edited by you" : "Confirmed",
            Icon: CheckCircle, cls: "bg-[#DCFCE7] text-[#166534]" }}
            testId={`line-confirmed-${line.id}`} />
        ) : null}
      </div>

      {line.review_reason && (
        <div className="px-4 pt-3">
          <Guidance title="Pulse needs you to confirm this" tone="warn">
            {line.review_reason}
          </Guidance>
        </div>
      )}

      <div className="px-4 py-3 space-y-3">
        <div>
          <Label htmlFor={`cat-${line.id}`} className="text-[11px] font-bold">
            What is this charge?
          </Label>
          <select
            id={`cat-${line.id}`}
            className="mt-1 w-full border border-[#D4D4D4] h-9 px-2 text-xs bg-white"
            value={form.commercial_category}
            onChange={(e) => setForm({ ...form, commercial_category: e.target.value })}
            data-testid={`import-line-category-${line.id}`}
          >
            {cats.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
          {chosen?.help && (
            <p className="text-[11px] text-[#525252] mt-1.5 leading-relaxed">
              {chosen.help}
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label htmlFor={`billed-${line.id}`} className="text-[11px] font-bold">
              Amount invoiced now
            </Label>
            <Input
              id={`billed-${line.id}`}
              type="number" step="0.01"
              className="mt-1 h-9 text-xs"
              value={form.amount_invoiced}
              onChange={(e) => setForm({ ...form, amount_invoiced: e.target.value })}
              data-testid={`import-line-billed-${line.id}`}
            />
            <p className="text-[10px] text-[#A3A3A3] mt-1">
              What this line charged on this invoice.
            </p>
          </div>

          {recurring && (
            <div>
              <Label htmlFor={`annual-${line.id}`} className="text-[11px] font-bold">
                Annual recurring value
              </Label>
              <Input
                id={`annual-${line.id}`}
                type="number" step="0.01"
                className="mt-1 h-9 text-xs"
                value={form.annual_recurring_value}
                onChange={(e) => setForm({ ...form, annual_recurring_value: e.target.value })}
                data-testid={`import-line-annual-${line.id}`}
              />
              <p className="text-[10px] text-[#A3A3A3] mt-1">
                The FULL amount that should recur every year at renewal — not
                the part-year amount on this invoice.
              </p>
            </div>
          )}
        </div>

        {recurring && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label htmlFor={`users-${line.id}`} className="text-[11px] font-bold">
                Change in licensed users
              </Label>
              <Input
                id={`users-${line.id}`}
                type="number" step="1"
                placeholder="Leave blank if not a user change"
                className="mt-1 h-9 text-xs"
                value={form.licensed_user_delta}
                onChange={(e) => setForm({ ...form, licensed_user_delta: e.target.value })}
                data-testid={`import-line-users-${line.id}`}
              />
              <p className="text-[10px] text-[#A3A3A3] mt-1">
                People, not months. Leave blank if this charge does not change
                the number of licensed users.
              </p>
            </div>
            <div>
              <Label htmlFor={`target-${line.id}`} className="text-[11px] font-bold">
                How should this be recorded?
              </Label>
              <select
                id={`target-${line.id}`}
                className="mt-1 w-full border border-[#D4D4D4] h-9 px-2 text-xs bg-white"
                value={form.target_recurring_item_id}
                onChange={(e) => setForm({ ...form, target_recurring_item_id: e.target.value })}
                data-testid={`import-line-target-${line.id}`}
              >
                <option value="">Add as a new recurring item</option>
                {(recurringItems || []).map((i) => (
                  <option key={i.id} value={i.id}>
                    Increase “{i.description}”
                  </option>
                ))}
              </select>
              <p className="text-[10px] text-[#A3A3A3] mt-1">
                Either way the annual value and the user count are the same. A
                separate item keeps this invoice's change visible on its own.
              </p>
            </div>
          </div>
        )}

        {!recurring && (
          <Guidance title="This will not affect ARR or the next renewal">
            Pulse records the invoice and the commercial history. The charge is
            not added to the recurring position and will not appear in the next
            renewal.
          </Guidance>
        )}

        <div>
          <Label htmlFor={`note-${line.id}`} className="text-[11px] font-bold">
            Note (optional)
          </Label>
          <Input
            id={`note-${line.id}`}
            className="mt-1 h-9 text-xs"
            placeholder="Why you classified it this way"
            value={form.operator_note}
            onChange={(e) => setForm({ ...form, operator_note: e.target.value })}
            data-testid={`import-line-note-${line.id}`}
          />
        </div>

        {line.differs_from_ai && (
          <div className="text-[10px] text-[#737373]">
            Pulse originally proposed{" "}
            <strong>{line.ai_commercial_category_label}</strong>. Your
            classification is recorded alongside it.
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="text-[10px] text-[#737373]">
            {line.needs_review && !line.operator_corrected
              ? "This line needs your decision before the import can be applied."
              : line.operator_corrected
                ? "Saved. Change anything above and save again if needed."
                : "Pulse read this line without difficulty — no action needed "
                  + "unless something above is wrong."}
          </div>
          <Button
            type="button"
            onClick={save}
            disabled={busy}
            className={`bg-[${BLUE}] hover:bg-[#003ECC] text-white rounded-none h-8 text-[11px] uppercase font-bold tracking-wider`}
            data-testid={`import-line-save-${line.id}`}
          >
            {busy ? "Saving…" : line.operator_corrected ? "Save again" : "Save changes"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ClassifyStep({ session, vocabulary, onSaveLine, busy }) {
  const lines = session?.lines || [];
  const outstanding = outstandingLines(session);
  const currency = session?.currency_code;

  if (!lines.length) {
    return (
      <EmptyState
        title="No charges to classify yet"
        body={
          "Read the uploaded documents first. Pulse will list every charge it "
          + "finds so you can confirm what each one means."
        }
      />
    );
  }

  return (
    <div className="space-y-4">
      <Guidance title="What to do">
        Check what each charge means commercially and change anything that is
        wrong. The key distinction is between charges that{" "}
        <strong>recur every year</strong> and charges that happened{" "}
        <strong>once</strong> — only the first kind changes ARR and the next
        renewal. <strong>You do not need to save a line Pulse already read
        correctly</strong>; only lines marked “Needs review” require a decision.
      </Guidance>

      {session?.status === "previewed" && (
        <Guidance title="You have already previewed this import" tone="warn">
          Saving a change here recalculates the commercial impact, so you will
          need to confirm the figures again before applying.
        </Guidance>
      )}

      {outstanding.length > 0 && (
        <Guidance title={`${outstanding.length} charge(s) still need your attention`} tone="warn">
          Pulse could not determine these with confidence, or the invoice does
          not state enough to be sure. Confirm each one before continuing —
          the import cannot be applied while any remain.
        </Guidance>
      )}

      {lines.map((line) => (
        <LineEditor
          key={line.id}
          line={line}
          vocabulary={vocabulary}
          recurringItems={session?.recurring_items}
          currency={currency}
          onSave={onSaveLine}
          busy={busy}
        />
      ))}
    </div>
  );
}

/* ── Step 4 — Commercial impact ────────────────────────────────────── */

function ImpactStep({ session, onPreview, busy }) {
  const s = impactSummary(session);
  const currency = session?.currency_code;
  const fmt = (n) => money(n, currency);
  const before = session?.before_position || {};
  const after = session?.after_position || {};

  return (
    <div className="space-y-5">
      {(session?.initial_warnings || []).map((w, i) => (
        <Guidance key={i} title="Check this before applying" tone="warn">{w}</Guidance>
      ))}

      {session?.recorded_as_history_only && (
        <Guidance title="This will be recorded as later commercial history" tone="warn">
          The client&rsquo;s original billing specification has not yet been
          established, so this invoice is kept as history and nothing is
          applied to the current commercial position.
          {session.pending_impact?.has_pending_recurring && (
            <div className="mt-1.5">
              <strong>{session.pending_impact.message}</strong>{" "}
              It is recorded as unresolved and will not enter a renewal. Once
              the billing baseline is established, it can be interpreted
              correctly.
            </div>
          )}
        </Guidance>
      )}

      <Guidance title="What this shows">
        The commercial position as it is today, and as it will be once this
        import is applied. Check the two figures below before continuing.
      </Guidance>

      <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <BeforeAfter
          label="Annual recurring revenue"
          before={fmt(before.annual_recurring_value)}
          after={fmt(after.annual_recurring_value)}
          movement={s.arr}
          testId="impact-arr"
        />
        <BeforeAfter
          label="Licensed users"
          before={before.licensed_users ?? 0}
          after={after.licensed_users ?? 0}
          movement={s.users}
          testId="impact-users"
        />
      </dl>

      <div>
        <h4 className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3] mb-2">
          This invoice
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Tile label="Invoice total" value={fmt(s.invoiceTotal)}
                hint="What the document charged" />
          <Tile label="One-time amount" value={fmt(s.oneTimeAmount)}
                hint="No effect on ARR" />
          <Tile label="Recurring billed now" value={fmt(s.recurringBilledNow)}
                hint="Part of this invoice" />
          <Tile label="Annual recurring impact"
                value={formatDelta(s.annualRecurringImpact, fmt)}
                hint="What changes at renewal" emphasis />
        </div>
      </div>

      {s.billedDiffersFromRecurring && (
        <Guidance title="The amount billed is not the amount that recurs">
          This invoice charged{" "}
          <strong>{fmt(s.recurringBilledNow)}</strong> for recurring items, but
          their full annual value is{" "}
          <strong>{formatDelta(s.annualRecurringImpact, fmt)}</strong>. That is
          normal for a charge covering only part of a term. Pulse keeps the
          amount actually invoiced as evidence and uses the full annual value
          for future renewals.
        </Guidance>
      )}

      <div>
        <h4 className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3] mb-2">
          Next renewal basis
        </h4>
        <div className="border-2 border-[#E5E5E5] bg-white p-4">
          <div className="text-2xl font-black tracking-tight text-[#0A0A0A]">
            {fmt(after.annual_recurring_value)}
          </div>
          <p className="text-[11px] text-[#525252] mt-1 leading-relaxed">
            The next renewal will be prepared from this figure. One-time charges
            on this invoice are excluded by design and can never appear in it.
          </p>
        </div>
      </div>

      {(s.modules.changed || s.integrations.changed || s.services.changed) && (
        <div className="text-[11px] text-[#525252]">
          Modules {s.modules.from} → {s.modules.to} ·
          {" "}Integrations {s.integrations.from} → {s.integrations.to} ·
          {" "}Recurring services {s.services.from} → {s.services.to}
        </div>
      )}

      <div className="flex justify-end">
        <Button
          type="button"
          onClick={onPreview}
          disabled={busy}
          className={`bg-[${BLUE}] hover:bg-[#003ECC] text-white rounded-none h-9 text-[11px] uppercase font-bold tracking-wider`}
          data-testid="import-preview"
        >
          {busy ? "Calculating…" : "Confirm these figures"}
        </Button>
      </div>
    </div>
  );
}

/* ── Step 5 — Confirm ──────────────────────────────────────────────── */

function ConfirmStep({ session, vocabulary, onApply, applying }) {
  const [open, setOpen] = useState(false);
  const s = impactSummary(session);
  const currency = session?.currency_code;
  const fmt = (n) => money(n, currency);
  const blockers = session?.blockers || [];
  const applied = session?.status === "applied";
  const lines = (session?.lines || []).filter(
    (l) => l.commercial_category !== "SUPPORTING_EVIDENCE");

  if (applied) {
    return (
      <div className="space-y-4">
        <div className="border-2 border-[#166534] bg-[#DCFCE7] p-5 flex gap-3" role="status">
          <CheckCircle size={20} weight="fill" className="text-[#166534] shrink-0" aria-hidden="true" />
          <div>
            <div className="font-bold text-sm text-[#166534]">
              Import applied
            </div>
            <p className="text-xs text-[#166534] mt-1 leading-relaxed">
              {session.recorded_as_history_only
                ? "The invoice has been recorded as commercial history. The "
                  + "client's original billing specification has not yet been "
                  + "established, so ARR and the next renewal are unchanged. "
                  + "No invoice was created and no customer was contacted."
                : <>The client's commercial position has been updated. Annual
              recurring revenue is now {fmt(session.after_position?.annual_recurring_value)}{" "}
              and {session.after_position?.licensed_users ?? 0} users are
              licensed. No invoice was created and no customer was contacted.</>}
            </p>
          </div>
        </div>
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <BeforeAfter
            label="Annual recurring revenue"
            before={fmt(session.before_position?.annual_recurring_value)}
            after={fmt(session.after_position?.annual_recurring_value)}
            movement={s.arr}
            testId="applied-arr"
          />
          <BeforeAfter
            label="Licensed users"
            before={session.before_position?.licensed_users ?? 0}
            after={session.after_position?.licensed_users ?? 0}
            movement={s.users}
            testId="applied-users"
          />
        </dl>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Guidance title="Read this before applying">
        This is exactly what Pulse will do. Nothing has changed yet.
      </Guidance>

      <div className="border-2 border-[#E5E5E5] bg-white">
        <div className="px-4 py-3 border-b border-[#F0F0F0] bg-[#FAFAFA] text-[10px] font-bold uppercase tracking-wider text-[#737373]">
          What will happen
        </div>
        <div className="divide-y divide-[#F0F0F0]">
          {lines.map((l) => (
            <div key={l.id} className="px-4 py-3" data-testid={`confirm-line-${l.id}`}>
              <div className="font-bold text-xs text-[#0A0A0A]">{l.description}</div>
              <div className="text-[11px] text-[#525252] mt-0.5">
                {l.commercial_category_label}
              </div>
              <ul className="mt-1.5 space-y-0.5">
                {describeLine(l, fmt).map((part, i) => (
                  <li key={i} className="text-[11px] text-[#0A0A0A]">{part}</li>
                ))}
              </ul>
            </div>
          ))}
          <div className="px-4 py-3 bg-[#FAFAFA]">
            <div className="text-[11px] text-[#0A0A0A]">
              <strong>Annual recurring revenue:</strong> {fmt(s.arr.from)} →{" "}
              <strong>{fmt(s.arr.to)}</strong>
            </div>
            <div className="text-[11px] text-[#0A0A0A] mt-0.5">
              <strong>Licensed users:</strong> {s.users.from} →{" "}
              <strong>{s.users.to}</strong>
            </div>
          </div>
        </div>
      </div>

      {session?.recorded_as_history_only && (
        <Guidance title="Recorded as history — the original billing position stays unknown"
                  tone="warn">
          This invoice has been recorded as later commercial history. The
          client&rsquo;s original billing specification has not yet been
          established, so ARR, licensed users and the next renewal are all
          unchanged.
          {session.pending_impact?.has_pending_recurring && (
            <div className="mt-1.5">{session.pending_impact.message}</div>
          )}
        </Guidance>
      )}

      {session?.rebase?.requested && (
        <Guidance title="This will replace the recorded original sale" tone="warn">
          The existing initial specification will be retired and kept as
          provenance of the correction; its invoice remains on file as
          supporting history. Exactly one original sale will remain.
        </Guidance>
      )}

      {(session?.initial_warnings || []).map((w, i) => (
        <Guidance key={`w${i}`} title="Check this before applying" tone="warn">
          {w}
        </Guidance>
      ))}

      {session?.mode === "bootstrap" && (
        <Guidance
          title={session.will_create_initial_specification
            ? "This will record what was originally sold"
            : "This will NOT record what was originally sold"}
          tone={session.will_create_initial_specification ? "warn" : "info"}>
          {session.will_create_initial_specification
            ? "You marked one of these documents as the original sale, so Pulse "
              + "will create an Initial Billing Specification from it. That "
              + "record is permanent once applied."
            : "No document was marked as the original sale, so Pulse makes no "
              + "claim about how this relationship began. The invoice is "
              + "recorded as history and the initial specification stays empty."}
        </Guidance>
      )}

      <div className="border-2 border-[#166534] bg-[#F0FDF4] p-4">
        <div className="text-[10px] font-bold uppercase tracking-wider text-[#166534]">
          What will NOT happen
        </div>
        <ul className="mt-2 space-y-1">
          {(session?.guarantees || vocabulary?.guarantees || []).map((g, i) => (
            <li key={i} className="text-[11px] text-[#166534] flex gap-1.5">
              <CheckCircle size={12} weight="bold" className="mt-0.5 shrink-0" aria-hidden="true" />
              {g}
            </li>
          ))}
        </ul>
      </div>

      {blockers.length > 0 && (
        <Guidance title="This import cannot be applied yet" tone="stop">
          <ul className="space-y-1 mt-1">
            {blockers.map((b, i) => <li key={i}>• {b}</li>)}
          </ul>
        </Guidance>
      )}

      <div className="flex justify-end">
        <Button
          type="button"
          onClick={() => setOpen(true)}
          disabled={!session?.can_apply || applying}
          className="bg-[#166534] hover:bg-[#14532D] text-white rounded-none h-10 px-6 text-[11px] uppercase font-bold tracking-wider"
          data-testid="import-apply"
        >
          {applying ? "Applying…" : "Apply to commercial record"}
        </Button>
      </div>

      <Dialog open={open} onOpenChange={(v) => !applying && setOpen(v)}>
        <DialogContent data-testid="import-apply-confirm">
          <DialogHeader>
            <DialogTitle>Apply this import?</DialogTitle>
            <DialogDescription>
              The client's commercial position will change from{" "}
              {fmt(s.arr.from)} to {fmt(s.arr.to)} a year, and from{" "}
              {s.users.from} to {s.users.to} licensed users. No invoice will be
              generated and no customer will be contacted.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={applying}>
              Cancel
            </Button>
            <Button
              onClick={async () => { await onApply(); setOpen(false); }}
              disabled={applying}
              className="bg-[#166534] hover:bg-[#14532D] text-white"
              data-testid="import-apply-confirm-button"
            >
              {applying ? "Applying…" : "Yes, apply"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ── The wizard ────────────────────────────────────────────────────── */

export default function InvoiceImportWizard({ clientId, onDone }) {
  const [session, setSession] = useState(null);
  const [vocabulary, setVocabulary] = useState(null);
  const [step, setStep] = useState("upload");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [applying, setApplying] = useState(false);
  const [unavailable, setUnavailable] = useState(null);
  const [error, setError] = useState(null);

  const detail = (e, fallback) => {
    const d = e?.response?.data?.detail;
    if (typeof d === "string") return d;
    if (d?.message) return d.message;
    return fallback;
  };

  const refresh = useCallback(async (importId) => {
    const r = await api.get(`/billing-import/imports/${importId}`);
    setSession(r.data);
    return r.data;
  }, []);

  // One session per visit. Deliberately created up front rather than on
  // first upload: the id must exist before a file can be attached to it,
  // and a half-uploaded file with nowhere to live is not recoverable.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError(null); setUnavailable(null);
      try {
        const [v, created] = await Promise.all([
          api.get("/billing-import/vocabulary"),
          api.post(`/billing-import/clients/${clientId}/imports`, { mode: "auto" }),
        ]);
        if (cancelled) return;
        setVocabulary(v.data);
        const full = await api.get(`/billing-import/imports/${created.data.id}`);
        if (cancelled) return;
        setSession(full.data);
        setStep(furthestStep(full.data));
      } catch (e) {
        if (cancelled) return;
        // 503 means the migration has not run — a configuration state,
        // not a failure, and it must not read like one.
        if (e.response?.status === 503) setUnavailable(detail(e, "Not available."));
        else setError(detail(e, "Could not start an invoice import."));
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [clientId]);

  const upload = async (files) => {
    if (!session) return;
    setBusy(true);
    let uploaded = 0;
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        await api.post(`/billing-import/imports/${session.id}/files`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded += 1;
      } catch (e) {
        toast.error(detail(e, `'${file.name}' could not be uploaded.`));
      }
    }
    if (uploaded) {
      const s = await refresh(session.id);
      toast.success(
        `${uploaded} document${uploaded === 1 ? "" : "s"} added. Now read them.`);
      // Land the operator on the step that has the next action. Without
      // this the file simply appears in a list and NOTHING on screen
      // says what to do next — the wizard reads as finished when it has
      // barely started.
      setStep(furthestStep(s));
    }
    setBusy(false);
  };

  const acknowledgeDuplicate = async (f) => {
    if (!session) return;
    setBusy(true);
    try {
      await api.patch(
        `/billing-import/imports/${session.id}/files/${f.id}`,
        { acknowledge_duplicate: true });
      await refresh(session.id);
      toast.success(`'${f.file_name}' confirmed as a distinct document.`);
    } catch (e) {
      toast.error(detail(e, "That document could not be confirmed."));
    }
    setBusy(false);
  };

  const nominateInitial = async (f, nominate) => {
    if (!session) return;
    setBusy(true);
    try {
      // Send the ORIGIN ruling, not a classification. Answering "no"
      // used to overwrite commercial_role with "other", discarding a
      // correct reading — a document properly identified as one-time
      // service came back classified as nothing in particular. The two
      // decisions are independent and the API now keeps them so.
      await api.patch(
        `/billing-import/imports/${session.id}/files/${f.id}`,
        { is_initial_subscription: !!nominate });
      await refresh(session.id);
      toast.success(nominate
        ? `'${f.file_name}' marked as the original sale.`
        : `'${f.file_name}' is no longer marked as the original sale.`);
    } catch (e) {
      toast.error(detail(e, "That could not be saved."));
    }
    setBusy(false);
  };

  const removeFile = async (f) => {
    if (!session) return;
    setBusy(true);
    try {
      await api.delete(`/billing-import/imports/${session.id}/files/${f.id}`);
      const s = await refresh(session.id);
      setStep(furthestStep(s));
      toast.success(`'${f.file_name}' removed.`);
    } catch (e) {
      toast.error(detail(e, "The document could not be removed."));
    }
    setBusy(false);
  };

  const interpret = async (refreshAll) => {
    if (!session) return;
    setBusy(true);
    try {
      const r = await api.post(
        `/billing-import/imports/${session.id}/interpret`,
        null, { params: { refresh: !!refreshAll } });
      const s = await refresh(session.id);
      const { interpreted = 0, failed = 0 } = r.data || {};
      if (failed) {
        toast.warning(
          `${interpreted} document(s) read; ${failed} could not be read.`);
      } else {
        toast.success(`${interpreted} document(s) read. Nothing has changed yet.`);
      }
      if (s.lines?.length) setStep("classify");
    } catch (e) {
      toast.error(detail(e, "The documents could not be read. Please try again."));
    }
    setBusy(false);
  };

  const saveLine = async (lineId, payload) => {
    if (!session) return;
    setBusy(true);
    try {
      await api.patch(
        `/billing-import/imports/${session.id}/lines/${lineId}`, payload);
      await refresh(session.id);
      toast.success("Line confirmed.");
    } catch (e) {
      toast.error(detail(e, "That change could not be saved."));
    }
    setBusy(false);
  };

  const preview = async () => {
    if (!session) return;
    setBusy(true);
    try {
      const r = await api.post(`/billing-import/imports/${session.id}/preview`);
      setSession(r.data);
      setStep("confirm");
    } catch (e) {
      toast.error(detail(e, "The commercial impact could not be calculated."));
    }
    setBusy(false);
  };

  const apply = async () => {
    if (!session) return;
    setApplying(true);
    try {
      await api.post(`/billing-import/imports/${session.id}/apply`);
      const s = await refresh(session.id);
      setSession(s);
      setStep("confirm");
      toast.success("The commercial position has been updated.");
      onDone?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.blockers?.length) {
        toast.error(d.blockers[0]);
        await refresh(session.id);
      } else {
        toast.error(detail(e, "The import could not be applied. Nothing was changed."));
      }
    }
    setApplying(false);
  };

  const download = (f) => {
    window.open(
      `${api.defaults.baseURL}/billing-import/imports/${session.id}/files/${f.id}/download`,
      "_blank", "noopener,noreferrer",
    );
  };

  if (loading) {
    return <div className="h-40 bg-[#F5F5F5] animate-pulse" data-testid="import-loading" />;
  }
  if (unavailable) {
    return (
      <EmptyState title="Invoice import is not available yet" body={unavailable} />
    );
  }
  if (error) {
    return (
      <EmptyState
        title="Could not start an invoice import"
        body={error}
        action={
          <Button
            onClick={() => window.location.reload()}
            className={`bg-[${BLUE}] hover:bg-[#003ECC] text-white rounded-none h-8 text-[11px] uppercase`}
            data-testid="import-retry"
          >
            <ArrowsClockwise size={13} weight="bold" className="mr-1" aria-hidden="true" />
            Try again
          </Button>
        }
      />
    );
  }

  const bootstrap = session?.mode === "bootstrap";
  const reachable = furthestStep(session);
  const reachableIndex = WIZARD_STEPS.findIndex((s) => s.id === reachable);
  const current = WIZARD_STEPS.find((s) => s.id === step) || WIZARD_STEPS[0];

  return (
    <div className="space-y-4" data-testid="invoice-import-wizard">
      <div>
        <h3 className="text-sm font-black uppercase tracking-wider text-[#0A0A0A]">
          {bootstrap ? "Build billing history from invoices" : "Import invoice"}
        </h3>
        <p className="text-[11px] text-[#525252] mt-1 max-w-3xl leading-relaxed">
          {bootstrap
            ? "This client has no structured commercial history in Pulse yet. "
              + "Upload the invoices you have and Pulse will read them, propose "
              + "what each one means commercially, and record it. Pulse will "
              + "only treat a document as \u201cwhat was originally sold\u201d if you "
              + "explicitly mark it as such \u2014 it never infers that from dates."
            : "Record an invoice that has already been sent to the customer. "
              + "Pulse separates the amount invoiced today from the amount that "
              + "should recur at renewal, and updates the commercial position "
              + "accordingly. It does not create or send an invoice."}
        </p>
      </div>

      <div role="tablist" aria-label="Import steps"
           className="flex flex-wrap gap-0 border-b border-[#E5E5E5]">
        {WIZARD_STEPS.map((s, i) => {
          const locked = i > reachableIndex && session?.status !== "applied";
          return (
            <button
              key={s.id}
              role="tab"
              type="button"
              aria-selected={step === s.id}
              disabled={locked}
              onClick={() => setStep(s.id)}
              className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border-b-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[${BLUE}] ${
                step === s.id
                  ? `border-[${BLUE}] text-[${BLUE}]`
                  : locked
                    ? "border-transparent text-[#D4D4D4] cursor-not-allowed"
                    : "border-transparent text-[#525252] hover:text-[#0A0A0A]"
              }`}
              data-testid={`import-step-${s.id}`}
            >
              <span className="mr-1.5 text-[10px]">{i + 1}</span>
              {s.label}
            </button>
          );
        })}
      </div>

      <p className="text-[11px] text-[#737373]">{current.hint}</p>

      <div role="tabpanel">
        {step === "upload" && (
          <UploadStep session={session} vocabulary={vocabulary} busy={busy}
                      onUpload={upload} onRemove={removeFile}
                      onAcknowledge={acknowledgeDuplicate}
                      onContinue={() => setStep("interpret")} />
        )}
        {step === "interpret" && (
          <InterpretStep session={session} busy={busy}
                         onInterpret={interpret} onDownload={download}
                         onAcknowledge={acknowledgeDuplicate}
                         onNominate={nominateInitial} />
        )}
        {step === "classify" && (
          <ClassifyStep session={session} vocabulary={vocabulary} busy={busy}
                        onSaveLine={saveLine} />
        )}
        {step === "impact" && (
          <ImpactStep session={session} busy={busy} onPreview={preview} />
        )}
        {step === "confirm" && (
          <ConfirmStep session={session} vocabulary={vocabulary}
                       applying={applying} onApply={apply} />
        )}
      </div>
    </div>
  );
}
